import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {BaseValue} from '@mintdata/mint-kit/api/values/BaseValue';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import {serializable} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';
import * as moment from 'moment';
import {Moment} from 'moment';

type ParsedString = {year:number, month:number, day:number};

// read for context: https://docs.microsoft.com/en-us/office/troubleshoot/excel/wrongly-assumes-1900-is-leap-year

// const EPOCH_START_DATE = moment.utc('1900-01-01');
// const EPOCH_START_DATE = moment('1900-01-01');

// const INVALID_LEAP_DAY_PASSED = moment.utc('1900-03-01');
// const INVALID_LEAP_DAY_PASSED = moment('1900-03-01');

const oneSecondMilliseconds = 1000;
const oneMinuteMilliseconds = 60 * oneSecondMilliseconds;
const oneHourMilliseconds = 60 * oneMinuteMilliseconds;
const oneDayMilliseconds = 24 * oneHourMilliseconds;

function _parseString(value:string):ParsedString
{
    if( !value )
        return null;

    const match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if( !match )
        return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if( isNaN(year) || isNaN(month) || isNaN(day) )
        return null;

    //check for date overflows
    const result = new Date(year, month - 1, day);
    if( result.getFullYear() != year || result.getMonth() != month - 1 || result.getDate() != day )
        return null;

    return {year, month, day};
}

function _zeroPad(value:number, minLength = 2):string
{
    const sValue = `${value}`;
    if( !sValue || sValue.length >= minLength )
        return sValue;

    return sValue.padStart(minLength, '0');
}

export class ADate extends BaseValue
{
    // ISO 8601 value, short format, i.e. "YYYY-MM-DD"
    @serializable public readonly value:string;

    public constructor(value:string)
    {
        super(Type.Date);

        if( value )
        {
            let pd = _parseString(value);
            Assert.yes(pd, `Incorrect value format: ${value}`);

            this.value = `${_zeroPad(pd.year, 4)}-${_zeroPad(pd.month)}-${_zeroPad(pd.day)}`;
        }
        else
            this.value = ADate.now().value;
    }

    public toString():string
    {
        return this.value;
    }

    public toValue():string
    {
        return `{"value":"${this.value}"}`;
    }

    public toMoment():Moment
    {
        // return moment.utc(this.value);
        return moment(this.value);
    }

    public toJSDate():Date
    {
        return moment(this.value).toDate();
    }

    public isEqualTo(other:IValue):boolean
    {
        if( !other ) return false;
        if( !(other instanceof ADate) ) return false;
        const otherDate = other as ADate;
        return this.offsetFromEpoch === otherDate.offsetFromEpoch;
    }

    public compareTo(other:IValue):number
    {
        if( !other ) return 1;
        if( Type.isInstanceOf(other, Type.Error) || Type.isInstanceOf(other, Type.Empty) )
        {
            return 1;
        }
        if( this.isEqualTo(other) )
            return 0;
        if( other instanceof ADate )
        {
            return (this.offsetFromEpoch > other.offsetFromEpoch) ? 1 : -1;
        }
        return -1;
    }

    public clone():IValue
    {
        return new ADate(this.value);
    }

    public get year():number
    {
        return _parseString(this.value).year;
    }

    // !! 1-based
    public get month():number
    {
        return _parseString(this.value).month;
    }

    public get day():number
    {
        return _parseString(this.value).day;
    }

    public get offsetFromEpoch():number
    {
        return this.toJSDate().getTime();
    }

    public addYears(years:number)
    {
        const year = this.year + years;
        const month = this.month;
        const day = this.day;
        return new ADate(`${year}-${month}-${day}`);
    };

    public static now():ADate
    {
        return this.fromJSDate(new Date());
    }

    public static fromJSDate(value:Date):ADate
    {
        const year = value.getFullYear();
        const month = value.getMonth() + 1;
        const day = value.getDate();
        return new ADate(`${year}-${month}-${day}`);
    }

    public static fromString(value:string):ADate | AError
    {
        if( !_parseString(value) )
            return new AError('Incorrect ADate format: ' + value);

        return new ADate(value);
    }

    public static circularRefResolver(value:string):IValue
    {
        // KEEP_ALIVE! Fix for resolving import circular dependencies for AError and ABaseValue
        return new AError('ERROR: Deprecated');
    }

    public toExcelDate():number
    {
        return Math.ceil(ADate._daysFrom1900(this.toMoment()));
    }

    public static _daysFrom1900(aMoment:Moment):number
    {
        // let delta = aMoment.diff(EPOCH_START_DATE, 'days', true) + 1;
        // if( !aMoment.isBefore(INVALID_LEAP_DAY_PASSED) )
        //     delta++;
        //
        // return delta;
        const date = aMoment;

        // const returnDateTime = 25569.0 + ((date.getTime() - (date.getTimezoneOffset() * oneMinuteMilliseconds)) / oneDayMilliseconds);
        const returnDateTime = 25569.0 + ((date.unix() * 1000 - (date.utcOffset() * oneMinuteMilliseconds)) / oneDayMilliseconds);

        return returnDateTime;
    }
}
